<template>
  <div class="login-container">
    <div class="login-stage">
      <div class="login" v-if="!loading">
        <div class="login-form">
          <div class="auth-heading">
            <span class="auth-heading__eyebrow">Adereso BSP</span>
            <h1>Welcome back</h1>
            <p>Manage your WhatsApp Business operation in one place.</p>
          </div>
          <AInput
            v-model="email"
            type="email"
            label="Email address"
            placeholder="name@company.com"
            prepend-inner-icon="tabler:mail"
          />
          <AInput
            v-model="password"
            type="password"
            label="Password"
            placeholder="Enter your password"
            prepend-inner-icon="tabler:lock"
            show-visibility-toggle
            @keyup.enter="loginWithEmailAndPassword"
          />
          <AButton color="blue" block @click="loginWithEmailAndPassword">
            Sign in
          </AButton>
          <div class="alternative-option">
            Don't have an account yet?
            <span @click="moveToRegister">Create account</span>
          </div>
        </div>
        <div class="alternative-login-options">
          <span class="auth-divider">You can also continue with</span>
          <AButton variant="outlined" color="base" block prepend-icon="tabler:brand-google" @click="loginWithGoogle">
            Google
          </AButton>
          <AButton variant="outlined" color="blue" block prepend-icon="tabler:brand-facebook" @click="loginWithFacebook">
            Facebook
          </AButton>
        </div>
        <!-- Alertas o mensajes adicionales -->
        <div
          class="alert alert-warning alert-dismissible fade show mt-5 d-none"
          role="alert"
          id="alert_1"
        >
          <!-- Mensaje de alerta -->
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="alert"
            aria-label="Close"
          ></button>
        </div>
      </div>
      <SpinnerLoader v-else />
    </div>

    <footer class="auth-footer" aria-label="Adereso BSP information and resources">
      <nav class="auth-footer__resources" aria-label="Adereso resources">
        <a
          v-for="resource in footerResources"
          :key="resource.label"
          :href="resource.href"
          target="_blank"
          rel="noopener noreferrer"
        >
          {{ resource.label }}
        </a>
      </nav>

      <hr />

      <div class="auth-footer__bottom">
        <nav class="auth-footer__legal" aria-label="Legal information">
          <a
            v-for="link in legalLinks"
            :key="link.label"
            :href="link.href"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ link.label }}
          </a>
        </nav>

        <p>Adereso - Omnichannel customer service software</p>
        <p>v{{ appVersion }}</p>
      </div>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { getAuth, signInWithEmailAndPassword, GoogleAuthProvider, FacebookAuthProvider, signInWithPopup } from 'firebase/auth'
import { POSITION, useToast } from 'vue-toastification'
import AuthService from '../services/AuthService/AuthService'
import { useRouter, useRoute } from 'vue-router'
import { onMounted, ref } from 'vue'
import SpinnerLoader from '../components/loaders/SpinnerLoader.vue'
import { AButton, AInput } from '@adereso-ai/vue-components'

const router = useRouter()
const route = useRoute()
const toast = useToast()
const email = ref('')
const password = ref('')
const loading = ref(true)
const appVersion = process.env.VUE_APP_VERSION || '0.1.0'

const footerResources = [
  {
    label: 'Help Center',
    href: 'https://help.adereso.ai/'
  },
  {
    label: 'Platform Status',
    href: 'https://adereso.statuspage.io/'
  },
  {
    label: 'Blog',
    href: 'https://adereso.ai/blog/'
  }
]

const legalLinks = [
  {
    label: 'Privacy Policy',
    href: 'https://adereso.ai/politica-de-privacidad/'
  },
  {
    label: 'Terms and Conditions',
    href: 'https://adereso.ai/terminos-y-condiciones/'
  }
]

onMounted(async () => {
  await router.isReady()
  const authService = await AuthService.getInstance()
  if (authService.currentUser.value != null) {
    router.push({ name: 'home', query: route.query })
  }
  loading.value = false
})

async function loginWithEmailAndPassword () {
  const auth = getAuth()
  loading.value = true

  if (email.value === '' || password.value === '') {
    toastError('Email and password are required')
    loading.value = false
    return
  }

  signInWithEmailAndPassword(auth, email.value, password.value)
    .then((result) => {
      const user = result.user
      handleUserLogin(user)
    })
    .catch((error) => {
      toastError(error)
      loading.value = false
    })
}

function moveToRegister () {
  router.push('/register')
}

async function loginWithGoogle () {
  const auth = getAuth()
  const provider = new GoogleAuthProvider()
  loading.value = true

  signInWithPopup(auth, provider)
    .then((result) => {
      // The user is signed in with Google.
      const user = result.user
      handleUserLogin(user)
    })
    .catch((error) => {
      toastError(error)
      loading.value = false
    })
}

async function loginWithFacebook () {
  const auth = getAuth()
  const provider = new FacebookAuthProvider()
  loading.value = true

  signInWithPopup(auth, provider)
    .then((result) => {
      // The user is signed in with Facebook.
      const user = result.user
      handleUserLogin(user)
    })
    .catch((error) => {
      toastError(error)
      loading.value = false
    })
}

async function handleUserLogin (user: any) {
  const token = await user.getIdToken()
  // send token to backend
  const authService = await AuthService.getInstance(token)
  await authService.loginWithIdToken()
  // redirect to dashboard
  router.push('/home')
}

function toastError (errorMessage: string) {
  try {
    toast.error(String(errorMessage), {
      position: POSITION.BOTTOM_LEFT
    })
  } catch (error) {
    console.log('error on toast:', error)
  }
}

</script>
